import api from '@app/api'

import { CONTENT_TYPES, FILE_TYPES } from '@app/api/constants'

import { CreateTaskResultsCommentParams } from './types'

import type { MultiStatusResponseDto, ResponseDto } from '@app/api/dtos'
import type {
  MonitoringTaskDto,
  ResultsDto,
  MonitoringTaskResultItemsDto,
  ResultPhotoDto,
  PhotoUploadLinkInfoMapDto,
  MonitoringTaskLogsDto,
  ExportAllMonitoringTasksData,
  MonitoringTaskResultItemCommentsItemDto
} from './dtos'
import type {
  GetTasksParams,
  GetResultsParams,
  UploadParams,
  CreateTasksData,
  GetTaskResultsParams,
  ExportListByIdsParams,
  GetTasksItemPhotosParams,
  PatchTaskResultsItemParams,
  CreateAllTasksData,
  ExportExcel,
  CommonPatchTasksData,
  GetPhotoUploadLinksParams,
  DeleteTaskResultsItemCommentData,
  PatchTaskResultsItemComment,
  MonitoringTasksFilerData,
  SendNotificationsSelectedData,
  PatchTaskTotalRunParametersData,
  ExportAllMonitoringsRegistryData,
  PostMonitoringTasksTotalData,
  PostMonitoringTasksTotalAllData,
  ExportAllMonitoringResultsData,
  RestartMonitoringTaskData,
  ChangeExternalProductParams,
  ChangePricesData,
  ExportTaskValidationsTechCheckParams,
  PatchTaskResultsItemPriceCheckedData
} from './types'
import type { ObjectValuesType } from '@app/types'
import type { RetrieveReport } from '@app/entities/report'

const URL = '/monitoring-tasks/'

export const getTasks = async (params?: GetTasksParams): Promise<ResponseDto<MonitoringTaskDto>> => {
  const response = await api.get<ResponseDto<MonitoringTaskDto>>(URL, { params })
  return response.data
}

export const getMonitoringTaskLogs = async (id: number): Promise<MonitoringTaskLogsDto[]> => {
  const response = await api.get<MonitoringTaskLogsDto[]>(`${URL}${id}/logs/`)
  return response.data
}

export const getTaskItem = async (id: number): Promise<MonitoringTaskDto> => {
  const response = await api.get<MonitoringTaskDto>(`${URL}${id}/`)
  return response.data
}

export const getResults = async (params?: GetResultsParams): Promise<ResponseDto<ResultsDto>> => {
  const response = await api.get<ResponseDto<ResultsDto>>(`${URL}results/`, { params })
  return response.data
}

export const getTaskResults = async (
  params: GetTaskResultsParams
): Promise<ResponseDto<MonitoringTaskResultItemsDto>> => {
  const { id, ...queryParams } = params

  const response = await api.get<ResponseDto<MonitoringTaskResultItemsDto>>(`${URL}${id}/results/`, {
    params: queryParams
  })
  return response.data
}

export const getTaskResultsItem = async (id: number): Promise<MonitoringTaskResultItemsDto> => {
  const response = await api.get<MonitoringTaskResultItemsDto>(`${URL}results/${id}/`)
  return response.data
}

export const getTaskResultsItemComments = async (id: number): Promise<MonitoringTaskResultItemCommentsItemDto[]> => {
  const url = `${URL}results/${id}/comments/`

  const response = await api.get<MonitoringTaskResultItemCommentsItemDto[]>(url)
  return response.data
}

export const deleteTaskResultsItemComment = async (data: DeleteTaskResultsItemCommentData): Promise<void> => {
  const { id } = data

  const url = `${URL}results/comments/${id}/`

  await api.delete(url)
}

export const patchTaskResultsItemComment = async (data: PatchTaskResultsItemComment): Promise<void> => {
  const { id, ...restData } = data

  const url = `${URL}results/comments/${id}/`

  await api.patch(url, { ...restData })
}

export const patchTaskResultsItem = async (params: PatchTaskResultsItemParams): Promise<void> => {
  const { id, data } = params

  const response = await api.patch(`${URL}results/${id}/`, data)
  return response.data
}

export const createTaskResultsComment = async (params: CreateTaskResultsCommentParams): Promise<void> => {
  const { id, data } = params

  const response = await api.post(`${URL}results/${id}/comments/`, data)
  return response.data
}

export const patchTaskResultsItemPriceChecked = async (data: PatchTaskResultsItemPriceCheckedData): Promise<void> => {
  const response = await api.patch(`${URL}results/price/checked/`, data)
  return response.data
}

export const changeExternalProduct = async (params: ChangeExternalProductParams): Promise<void> => {
  const { id, ...data } = params

  const response = await api.patch(`${URL}results/${id}/external-product`, data)
  return response.data
}

export const getTasksItemPhotos = async (params: GetTasksItemPhotosParams): Promise<ResponseDto<ResultPhotoDto>> => {
  const { id, ...queryParams } = params

  const response = await api.get<ResponseDto<ResultPhotoDto>>(`${URL}${id}/results/photos/`, { params: queryParams })
  return response.data
}

export const exportTemplateMonitoringLists = async (
  data: {
    file: ObjectValuesType<typeof FILE_TYPES>
  },
  signal?: AbortSignal
): Promise<Blob> => {
  const response = await api.post<Blob>(`${URL}results/mass-patch/export/`, data, {
    signal,
    responseType: 'blob'
  })

  return response.data
}

export const createTasks = async (data: CreateTasksData): Promise<ResponseDto<MonitoringTaskDto>> => {
  const response = await api.post<ResponseDto<MonitoringTaskDto>>(URL, data)
  return response.data
}

export const createAllTasks = async (data: CreateAllTasksData): Promise<void> => {
  return await api.post(`${URL}bulk-start/`, data)
}

export const completeTask = async (data: UploadParams): Promise<void> => {
  const { id } = data

  await api.patch(`${URL}${id}/complete/`, data)
}

export const completeCorrectionTask = async (data: UploadParams): Promise<void> => {
  const { id } = data

  await api.patch(`${URL}${id}/results/corrections/complete/`, data)
}

export const matchTask = async (params: UploadParams): Promise<void> => {
  const { id } = params

  await api.post(`${URL}${id}/matching/`)
}

export const uploadPhotos = async (params: UploadParams, data: FormData): Promise<void> => {
  const { id } = params

  await api.post(`${URL}${id}/photos/`, data, {
    headers: {
      'Content-Type': CONTENT_TYPES.multipartFormData
    }
  })
}

export const postUploadPrices = async ({ id, data }: ChangePricesData): Promise<MultiStatusResponseDto> => {
  const response = await api.post(`${URL}${id}/import/`, data, {
    headers: {
      'Content-Type': CONTENT_TYPES.multipartFormData
    }
  })

  return response.data
}

export const postUpdatePrices = async ({ id, data }: ChangePricesData): Promise<MultiStatusResponseDto> => {
  const response = await api.post(`${URL}${id}/results/corrections/import/`, data, {
    headers: {
      'Content-Type': CONTENT_TYPES.multipartFormData
    }
  })

  return response.data
}

export const exportListByIds = async (data: ExportListByIdsParams, signal?: AbortSignal): Promise<Blob> => {
  const response = await api.post<Blob>(`${URL}export/`, data, {
    signal,
    responseType: 'blob'
  })

  return response.data
}

export const exportTaskValidationsTechCheck = async (
  params: ExportTaskValidationsTechCheckParams,
  signal?: AbortSignal
): Promise<Blob> => {
  const url = `${URL}${params.id}/validations/tech-check/export/`

  const response = await api.get<Blob>(url, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })

  return response.data
}

export const exportMatchingTemplate = async ({ id, ...params }: ExportExcel, signal?: AbortSignal): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}${id}/matching-template/export/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })

  return response.data
}

export const recoveryTasksItems = async (data: CommonPatchTasksData): Promise<void> => {
  // TODO: [mock] Заменить на рабочий url и сверить схему, когда бэк будет готов
  const response = await api.post(`${URL}restore/`, data)
  return response.data
}

export const stopTasksItems = async (data: CommonPatchTasksData): Promise<void> => {
  // TODO: [mock] Заменить на рабочий url и сверить схему, когда бэк будет готов
  const response = await api.post(`${URL}stop/`, data)
  return response.data
}

export const restartTasksItems = async (data: CommonPatchTasksData): Promise<void> => {
  // TODO: [mock] Заменить на рабочий url и сверить схему, когда бэк будет готов
  const response = await api.post(`${URL}restart/`, data)
  return response.data
}

export const restartTotalCheckItem = async (data: RestartMonitoringTaskData): Promise<void> => {
  const response = await api.post(`${URL}total-restart/`, data)
  return response.data
}

export const sendNotificationSelected = async (data: SendNotificationsSelectedData): Promise<void> => {
  return await api.post(`${URL}notification/`, data)
}

export const sendNotificationsEveryone = async (data: MonitoringTasksFilerData): Promise<void> => {
  return await api.post(`${URL}notification/filter/`, data)
}

export const exportAllMonitoringsRegistry = async (
  data: ExportAllMonitoringsRegistryData,
  signal?: AbortSignal
): Promise<Blob> => {
  const response = await api.post<Blob>(`${URL}report/export/`, data, {
    signal,
    responseType: 'blob'
  })

  return response.data
}

export const exportAllMonitoringResults = async (
  data: ExportAllMonitoringResultsData,
  signal?: AbortSignal
): Promise<RetrieveReport> => {
  const response = await api.post<RetrieveReport>(`${URL}results/export/vertical/`, data, { signal })

  return response.data
}

export const exportAllMonitoringTasks = async (
  data: ExportAllMonitoringTasksData,
  signal?: AbortSignal
): Promise<Blob> => {
  const response = await api.post<Blob>(`${URL}export/multiple/`, data, {
    signal,
    responseType: 'blob'
  })

  return response.data
}

export const exportCheckingPrices = async (
  { ...params }: Omit<ExportExcel, 'id'>,
  signal?: AbortSignal
): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}validations/price-check/export/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })

  return response.data
}

export const exportTechChecking = async (
  { ...params }: Omit<ExportExcel, 'id'>,
  signal?: AbortSignal
): Promise<Blob> => {
  const response = await api.get<Blob>(`${URL}validations/tech-check/export/`, {
    signal,
    params,
    responseType: 'blob',
    headers: {
      'Content-Type': CONTENT_TYPES.applicationOfficedocumentSpreadsheetml
    }
  })

  return response.data
}

export const getPhotoUploadLinks = async ({
  id,
  data
}: GetPhotoUploadLinksParams): Promise<PhotoUploadLinkInfoMapDto[]> => {
  const url = `${URL}${id}/photos/presigned/`
  const response = await api.post<{ results: PhotoUploadLinkInfoMapDto[] }>(url, data)

  // TODO: [LNT-2163] убрать '.results' так как бек сразу будет возвращать массив
  return response.data.results
}

export const patchTaskTotalRunParameters = async (data: PatchTaskTotalRunParametersData): Promise<void> => {
  return await api.patch(`${URL}total/run-parameters/`, data)
}

export const postMonitoringTasksTotal = async (data: PostMonitoringTasksTotalData): Promise<void> => {
  await api.post(`${URL}total/`, data)
}

export const postMonitoringTasksTotalAll = async (data: PostMonitoringTasksTotalAllData): Promise<void> => {
  await api.post(`${URL}total/filter/`, data)
}

export const importMonitoringTasksMassMatch = async (data: FormData): Promise<void> => {
  const url = `${URL}results/mass-patch/import/`
  const config = { headers: { 'Content-Type': CONTENT_TYPES.multipartFormData } }
  await api.post(url, data, config)
}
